import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Text, Button } from '@primer/react-brand'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'

import { API_BASE_URL } from '../../config/apiConfig'

// Shared Stripe card-capture form for the Civic Lens subscribe flow. Owns the
// whole card-only handshake: it creates its own setup-mode SetupIntent
// (POST /api/v2/organizations/setup_intent), renders Stripe's Card number /
// Expiry / CVC Elements plus Name + Billing ZIP, and confirms the card (handling
// 3D Secure inline). Card data is captured entirely inside Stripe's iframes — it
// never touches our server.
//
// The page decides what to do with the confirmed card: pass an async
// `onConfirmed(setupIntentId)` that performs the server call + navigation and
// resolves to an error string to display (or a falsy value on success, in which
// case it is expected to navigate away). Used by PaymentDetailsPage (subscribe)
// and UpdatePaymentMethodPage (change card).
//
// Props: { stripePublishableKey, submitLabel, secondaryAction: { label, onClick }, onConfirmed }

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

// Stripe Element field styling. Concrete color values are required here: the
// fields render inside Stripe's cross-origin iframe, which can't read the app's
// Primer CSS variables — the values below mirror the dark text / muted grey used
// on these screens' plain inputs.
const cardElementStyle = {
  base: {
    fontSize: '15px',
    fontFamily: 'inherit',
    color: '#1f2328',
    '::placeholder': { color: '#8c959f' },
  },
  invalid: { color: '#cf222e' },
}

// Inner form — must live inside <Elements> so it can use the Stripe hooks.
//
// `onRefreshIntent` mints a fresh SetupIntent. A SetupIntent is single-use: once
// confirmCardSetup succeeds it is spent, and any later confirm against the same
// client secret is rejected by Stripe ("unexpected state"). The server call that
// follows confirmation can still fail — most importantly the on-session charge
// for an already-approved org can be DECLINED after the card itself saved fine —
// so after any such failure the form asks for a new intent, and the user can
// try another card without reloading the page.
const CardFields = ({ submitLabel, secondaryAction, onConfirmed, clientSecret, onRefreshIntent }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [card, setCard] = useState({ name: '', zip: '' })
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  const setField = (field) => (e) => {
    if (error) setError(null)
    setCard((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handleSave = async () => {
    if (!stripe || !elements || loading) return

    if (!card.name.trim() || !card.zip.trim()) {
      setError('Please complete all card details.')
      return
    }
    if (!clientSecret) {
      setError('Secure card entry is still loading. Please try again in a moment.')
      return
    }

    setLoading(true)
    setError(null)

    // Confirm the SetupIntent from the card fields. Stripe renders any 3D Secure
    // challenge in a modal; we only hand off to the page on success.
    const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name: card.name,
          address: { postal_code: card.zip },
        },
      },
    })

    if (confirmError) {
      setError(confirmError.message || 'Your card could not be saved. Please try again.')
      // A spent or otherwise unusable intent cannot be retried; get a new one. A
      // plain card error (wrong number, expired) leaves the intent reusable.
      if (confirmError.code === 'setup_intent_unexpected_state' && onRefreshIntent) await onRefreshIntent()
      setLoading(false)
      return
    }

    if (!setupIntent || setupIntent.status !== 'succeeded') {
      setError('Your card could not be saved. Please try again.')
      setLoading(false)
      return
    }

    // Hand the confirmed SetupIntent to the page's server call. A returned string
    // is a user-facing error; otherwise the page is expected to navigate away, so
    // we stay in the loading state.
    const errorMessage = await onConfirmed(setupIntent.id)
    if (errorMessage) {
      setError(errorMessage)
      // The intent above is now spent. Without a fresh one, the next Save would
      // fail regardless of which card the user enters.
      if (onRefreshIntent) await onRefreshIntent()
      setLoading(false)
    }
  }

  const fieldLabelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--base-color-scale-gray-6)',
    display: 'block',
  }

  const elementWrapStyle = { marginTop: 4 }

  const textInputStyle = {
    width: '100%',
    marginTop: 4,
    padding: '8px 12px',
    fontSize: 15,
    fontFamily: 'inherit',
    color: 'var(--base-color-scale-gray-9)',
    border: '1px solid var(--base-color-scale-gray-1)',
    borderRadius: 6,
    outline: 'none',
    boxSizing: 'border-box',
  }

  return (
    <div>
      {/* Grouped card inputs — Stripe card-only Elements */}
      <div
        style={{
          border: '1px solid var(--base-color-scale-gray-1)',
          borderRadius: 6,
          overflow: 'hidden',
          marginBottom: 14,
          background: '#fff',
        }}
      >
        <div style={{ padding: '10px 13px', borderBottom: '1px solid var(--base-color-scale-gray-1)' }}>
          <span style={fieldLabelStyle}>Card number</span>
          <div style={elementWrapStyle}>
            <CardNumberElement options={{ style: cardElementStyle, placeholder: '1234 1234 1234 1234' }} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          <div style={{ padding: '10px 13px', borderRight: '1px solid var(--base-color-scale-gray-1)' }}>
            <span style={fieldLabelStyle}>Expiry</span>
            <div style={elementWrapStyle}>
              <CardExpiryElement options={{ style: cardElementStyle }} />
            </div>
          </div>
          <div style={{ padding: '10px 13px' }}>
            <span style={fieldLabelStyle}>CVC</span>
            <div style={elementWrapStyle}>
              <CardCvcElement options={{ style: cardElementStyle }} />
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <span style={fieldLabelStyle}>Name on card</span>
        <input
          type="text"
          style={textInputStyle}
          placeholder="Full name as it appears on card"
          value={card.name}
          onChange={setField('name')}
          aria-label="Name on card"
        />
      </div>
      <div style={{ marginBottom: 14 }}>
        <span style={fieldLabelStyle}>Billing ZIP / postal code</span>
        <input
          type="text"
          style={textInputStyle}
          placeholder="ZIP or postal code"
          value={card.zip}
          onChange={setField('zip')}
          aria-label="Billing ZIP or postal code"
        />
      </div>

      {error && (
        <Text as="p" size="100" style={{ margin: '0 0 14px', color: 'var(--brand-color-error-fg, #cf222e)' }}>
          {error}
        </Text>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={loading || !stripe}
          style={{
            backgroundColor: 'var(--base-color-scale-blue-5)',
            borderColor: 'var(--base-color-scale-blue-5)',
            borderRadius: '6px',
          }}
        >
          {loading ? 'Saving…' : submitLabel}
        </Button>
        {secondaryAction && (
          <span
            style={{ fontSize: 15, fontWeight: 500, color: 'var(--base-color-scale-gray-6)', cursor: 'pointer' }}
            onClick={secondaryAction.onClick}
          >
            {secondaryAction.label}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 16 }}>
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M4 7V5a4 4 0 0 1 8 0v2M3.5 7h9a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5v-6a.5.5 0 0 1 .5-.5Z"
            stroke="var(--base-color-scale-gray-4)"
            strokeWidth="1.3"
            strokeLinejoin="round"
          />
        </svg>
        <Text as="span" style={{ fontSize: 12, color: 'var(--base-color-scale-gray-4)' }}>
          Secured by Stripe
        </Text>
      </div>
    </div>
  )
}

const StripeCardForm = ({ stripePublishableKey, submitLabel, secondaryAction, onConfirmed }) => {
  const stripePromise = useMemo(
    () => (stripePublishableKey ? loadStripe(stripePublishableKey) : null),
    [stripePublishableKey],
  )

  const [clientSecret, setClientSecret] = useState(null)
  const [initError, setInitError] = useState(null)

  // Mint a SetupIntent and hold its client secret. Called once when the form
  // loads, and again after a failed save (see CardFields) because an intent is
  // spent the moment it is confirmed. Returns true on success.
  const createSetupIntent = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v2/organizations/setup_intent`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({}),
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok && data.client_secret) {
        setClientSecret(data.client_secret)
        return true
      }
    } catch (err) {
      // fall through
    }
    setClientSecret(null)
    return false
  }, [])

  // Create a SetupIntent as soon as the form loads so the client secret is ready
  // to confirm the card against when the user saves.
  useEffect(() => {
    if (!stripePromise) {
      setInitError('Card entry is unavailable right now. Please try again later.')
      return undefined
    }

    let cancelled = false
    ;(async () => {
      const ok = await createSetupIntent()
      if (!cancelled && !ok) setInitError('Could not start secure card entry. Please refresh and try again.')
    })()

    return () => { cancelled = true }
  }, [stripePromise, createSetupIntent])

  if (initError) {
    return (
      <Text as="p" size="100" style={{ margin: '0 0 14px', color: 'var(--brand-color-error-fg, #cf222e)' }}>
        {initError}
      </Text>
    )
  }

  if (!stripePromise) return null

  return (
    <Elements stripe={stripePromise}>
      <CardFields
        submitLabel={submitLabel}
        secondaryAction={secondaryAction}
        onConfirmed={onConfirmed}
        clientSecret={clientSecret}
        onRefreshIntent={createSetupIntent}
      />
    </Elements>
  )
}

export default StripeCardForm
